import { ApplicationModuleCode } from '@/domain/application-modules/application-module.interface';
import { ApplicationModulesService } from '@/domain/application-modules/application-modules.service';
import { FeatureCode } from '@/utils/disabled-features/disabled-features.interface';
import { DisabledFeaturesService } from '@/utils/disabled-features/disabled-features.service';
import { inject, Injectable } from '@angular/core';
import { type CanActivateChildFn, type CanActivateFn, Router } from '@angular/router';
import { combineLatest, type Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProfileProvider } from '../../providers/profile/profile.provider';
import { DEET_ADMIN_ACCESS, DEET_VIEWER_ACCESS } from '../../providers/rights.constants';

@Injectable({
    providedIn: 'root'
})
export class DeetFeatureService {
    private _router = inject(Router);
    private _disabledFeaturesService = inject(DisabledFeaturesService);
    private _profileProvider = inject(ProfileProvider);
    private _applicationModulesProvider = inject(ApplicationModulesService);

    goToFeature() {
        this._router.navigate(['/', 'deet']);
    }

    hasFeatureAccess$(): Observable<boolean> {
        return combineLatest([
            of(this._disabledFeaturesService.isFeatureEnabled(FeatureCode.DEET)),
            this._profileProvider.hasRight$(DEET_VIEWER_ACCESS),
            this._profileProvider.hasRight$(DEET_ADMIN_ACCESS),
            this._applicationModulesProvider.hasModuleAccess$(ApplicationModuleCode.HIDE_TO_ITALY)
        ]).pipe(
            map(([isFeatureEnabled, isUserHasViewerRight, isUserHasAdminRight, hideToItaly]) => {
                return isFeatureEnabled && (isUserHasViewerRight || isUserHasAdminRight) && !hideToItaly;
            })
        );
    }

    async hasGeneralAccess(): Promise<boolean> {
        const hasFeatureAccess = this._disabledFeaturesService.isFeatureEnabled(FeatureCode.DEET);
        const hasProfileRight = await Promise.all([
            this._profileProvider.hasRight(DEET_VIEWER_ACCESS),
            this._profileProvider.hasRight(DEET_ADMIN_ACCESS)
        ]);
        return hasFeatureAccess && hasProfileRight.some(profileAccess => profileAccess === true);
    }

    async hasAdminAccess(): Promise<boolean> {
        const hasFeatureAccess = this._disabledFeaturesService.isFeatureEnabled(FeatureCode.DEET);
        return hasFeatureAccess && this._profileProvider.hasRight(DEET_ADMIN_ACCESS);
    }
}

export const deetGuard: CanActivateFn & CanActivateChildFn = async () => {
    const featureService = inject(DeetFeatureService);
    return await featureService.hasGeneralAccess();
};
